/* Feb-2026 · Continuity & Character-Drift Review admin panel.
 *
 * Sequential chapter-by-chapter walk of a project looking for plot
 * gaps and character-voice drift, with a per-chapter rewrite/rework
 * option. Unlike Retroactive Review (independent/concurrent per
 * chapter), this walk is sequential server-side, so it runs as a
 * background job the panel polls while status is queued/running.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Loader2, Play, ChevronDown, ChevronRight, Compass, Wand2, BookUser, X, Gauge,
  TrendingUp, TrendingDown, Minus, ListChecks, Wrench, CalendarClock, History,
  ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { continuityReviewApi, modelsApi, adminApi, chaptersApi } from '@/lib/api';
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const SEVERITY_CLS = {
  low:      'border-amber-300 text-amber-800 dark:border-amber-800/40 dark:text-amber-200',
  moderate: 'border-orange-300 text-orange-800 dark:border-orange-800/40 dark:text-orange-200',
  severe:   'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200',
};

const HEALTH_BADGE = {
  not_reviewed: { label: 'Not reviewed',    cls: 'border-zinc-300 text-zinc-600 dark:border-zinc-700 dark:text-zinc-400' },
  reviewing:    { label: 'Reviewing…',      cls: 'border-sky-300 text-sky-800 dark:border-sky-800/40 dark:text-sky-200' },
  stalled:      { label: 'Stalled',         cls: 'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200' },
  error:        { label: 'Error',           cls: 'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200' },
  attention:    { label: 'Needs attention', cls: 'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200' },
  minor:        { label: 'Minor issues',    cls: 'border-amber-300 text-amber-800 dark:border-amber-800/40 dark:text-amber-200' },
  clean:        { label: 'Clean',           cls: 'border-emerald-300 text-emerald-800 dark:border-emerald-800/40 dark:text-emerald-200' },
};

// Most-crucial-first — the whole point of the badge is to tell the
// admin where to spend their attention without opening every project.
const BUCKET_PRIORITY = { attention: 0, error: 1, stalled: 2, minor: 3, reviewing: 4, not_reviewed: 5, clean: 6 };

const TREND_META = {
  improving: { Icon: TrendingUp,   cls: 'text-emerald-600 dark:text-emerald-400', label: 'Improving since the last walk' },
  worsening: { Icon: TrendingDown, cls: 'text-red-600 dark:text-red-400',         label: 'Worsening since the last walk' },
  steady:    { Icon: Minus,        cls: 'text-muted-foreground',                   label: 'Unchanged since the last walk' },
};

function HealthBadge({ bucket, score, reviewed, total, trend, previousScore }) {
  const meta = HEALTH_BADGE[bucket] || HEALTH_BADGE.not_reviewed;
  const trendMeta = trend ? TREND_META[trend] : null;
  return (
    <Badge variant="outline" className={`whitespace-nowrap text-[9.5px] ${meta.cls}`}>
      {bucket === 'reviewing' ? <Loader2 className="mr-1 inline h-2.5 w-2.5 animate-spin" /> : null}
      {meta.label}
      {bucket === 'reviewing' && total ? ` ${reviewed}/${total}` : ''}
      {typeof score === 'number' ? ` · ${score}%` : ''}
      {trendMeta && (
        <span title={`${trendMeta.label} (was ${previousScore}%, now ${score}%)`} className={`ml-1 inline-flex items-center ${trendMeta.cls}`}>
          <trendMeta.Icon className="h-2.5 w-2.5" />
        </span>
      )}
    </Badge>
  );
}

function ManuscriptsHealthTable({ onPick }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await continuityReviewApi.listManuscripts();
      const sorted = [...(r.manuscripts || [])].sort(
        (a, b) => (BUCKET_PRIORITY[a.health_bucket] ?? 9) - (BUCKET_PRIORITY[b.health_bucket] ?? 9),
      );
      setRows(sorted);
    } catch (e) { toast.error(String(e).slice(0, 160)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const needingAttention = (rows || []).filter((r) => ['attention', 'error', 'stalled'].includes(r.health_bucket)).length;

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="continuity-manuscripts-health-table">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-[13px] font-semibold">AI manuscript health</h3>
          <span className="text-[10.5px] text-muted-foreground">
            Every Loom novel, sorted so the ones most needing attention float to the top.
            {rows && needingAttention > 0 && (
              <span className="ml-1 font-medium text-red-700 dark:text-red-300">{needingAttention} need attention.</span>
            )}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="continuity-manuscripts-refresh">
          {loading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null} Refresh
        </Button>
      </div>
      <div className="max-h-72 overflow-y-auto rounded border border-border/60">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-panel-header/60">
            <tr className="text-left text-[10px] text-muted-foreground">
              <th className="px-2 py-1.5">Title</th>
              <th className="px-2 py-1.5">Author</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">Chapters</th>
              <th className="px-2 py-1.5">Health</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">No manuscripts found.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.manuscript_id} className="border-t border-border/40" data-testid={`continuity-manuscript-row-${r.manuscript_id}`}>
                <td className="max-w-[220px] truncate px-2 py-1.5 font-medium">{r.title}</td>
                <td className="max-w-[140px] truncate px-2 py-1.5 text-muted-foreground">{r.author || '—'}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{r.status}{r.live_on_bookstore ? ' · live' : ''}</td>
                <td className="px-2 py-1.5 font-mono-stat text-muted-foreground">{r.chapters_written ?? '—'}/{r.chapters_planned ?? '—'}</td>
                <td className="px-2 py-1.5">
                  <HealthBadge bucket={r.health_bucket} score={r.health_score} reviewed={r.chapters_reviewed} total={r.total_chapters} trend={r.health_trend} previousScore={r.previous_health_score} />
                  {r.needs_rework_count > 0 && (
                    <span className="ml-1 text-[9.5px] text-muted-foreground">({r.needs_rework_count} flagged)</span>
                  )}
                  {r.pacing_drift_count > 0 && (
                    <Badge variant="outline" className="ml-1.5 gap-0.5 border-amber-300 text-[9px] text-amber-800 dark:border-amber-800/40 dark:text-amber-200" data-testid={`continuity-manuscript-pacing-drift-${r.manuscript_id}`}>
                      <Gauge className="h-2.5 w-2.5" />
                      pacing drift · {r.pacing_drift_count}
                    </Badge>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[10.5px]"
                          onClick={() => onPick(r.project_id)}
                          data-testid={`continuity-manuscript-open-${r.manuscript_id}`}>
                    Open <ChevronRight className="ml-0.5 h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ContinuityDriftPanel() {
  const [pid, setPid]         = useState('');
  const [data, setData]       = useState(null);
  const [busy, setBusy]       = useState('');
  const [expanded, setExpanded] = useState({});
  const [reworking, setReworking] = useState(null); // chapter_id in flight
  const [preview, setPreview] = useState(null);      // {chapter_id, chapter_number, preview_id, status, original, rewritten}
  const [applying, setApplying] = useState(false);
  const [models, setModels] = useState([]);
  // continuity_review_model — persisted in app_config until admin changes it.
  // Loaded on mount; auto-saved on change. Controls both the walk and rework model.
  const [continuityModel, setContinuityModel] = useState('');
  const [modelSaving, setModelSaving] = useState(false);
  const [bibleChanges, setBibleChanges] = useState(null);
  const [rechecking, setRechecking] = useState(null); // chapter_id in flight
  const [dismissing, setDismissing] = useState(null); // character_id in flight
  const [bulkRecheck, setBulkRecheck] = useState({}); // character_id -> {bulk_id, status, total, results}
  // Chapter length warning — populated lazily when pid looks valid
  const [longChapters, setLongChapters] = useState([]);
  const pollRef = useRef(null);
  const reworkPollRef = useRef(null);
  const bulkPollRef = useRef({});

  useEffect(() => {
    modelsApi.adminList().then((r) => setModels(r?.models || [])).catch(() => {});
    // Load the persisted global continuity model from app_config on mount
    adminApi.config().then((cfg) => {
      if (cfg?.continuity_review_model) setContinuityModel(cfg.continuity_review_model);
    }).catch(() => {});
  }, []);

  // Fetch word counts when the pid looks like a real project ID (>= 10 chars)
  // so we can warn about chapters that may hit the 2-minute per-chapter ceiling.
  useEffect(() => {
    if (!pid || pid.length < 10) { setLongChapters([]); return; }
    const timer = setTimeout(async () => {
      try {
        const chs = await chaptersApi.list(pid);
        setLongChapters((chs || []).filter((c) => (c.words || 0) > 5000));
      } catch { setLongChapters([]); }
    }, 600);
    return () => clearTimeout(timer);
  }, [pid]);

  const load = async (silent, overridePid) => {
    const targetPid = overridePid || pid;
    if (!targetPid) return;
    if (!silent) setBusy('load');
    try {
      const r = await continuityReviewApi.get(targetPid);
      setData(r);
      return r;
    } catch (e) { if (!silent) toast.error(String(e).slice(0, 140)); }
    finally { if (!silent) setBusy(''); }
  };

  const loadBibleChanges = async (targetPid) => {
    try {
      const r = await continuityReviewApi.getBibleChanges(targetPid || pid);
      setBibleChanges(r);
    } catch (e) { /* non-fatal — banner just stays hidden */ }
  };

  const pickManuscript = (projectId) => {
    setPid(projectId);
    setData(null);
    setBibleChanges(null);
    load(false, projectId);
    loadBibleChanges(projectId);
  };

  // Poll while a job is queued/running.
  useEffect(() => {
    clearInterval(pollRef.current);
    const status = data?.job?.status;
    if (status === 'queued' || status === 'running') {
      pollRef.current = setInterval(() => load(true), 4000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.job?.status, pid]);

  // Persist model selection globally in app_config so it survives page refreshes.
  const handleModelChange = async (val) => {
    const m = val === '__auto' ? '' : val;
    // Validation toast — warn if the chosen model isn't in the loaded catalog.
    if (m && models.length > 0 && !models.some((mo) => mo.id === m)) {
      toast.warning(`"${m}" is not in the active model catalog. Saving anyway, but walks may fail if the model is retired.`);
    }
    setContinuityModel(m);
    setModelSaving(true);
    try {
      await adminApi.saveConfig({ continuity_review_model: m });
    } catch { /* non-fatal — selection still applies for this session */ }
    finally { setModelSaving(false); }
  };

  const start = async () => {
    if (!pid) return;
    setBusy('start');
    try {
      const r = await continuityReviewApi.start(pid, continuityModel || null);
      if (r.started === false) {
        toast.info(r.note || 'Nothing to review.');
      } else {
        toast.success(`Continuity walk started · ${r.job?.total_chapters || 0} chapters`);
      }
      await load();
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setBusy(''); }
  };

  const resume = async () => {
    if (!pid) return;
    setBusy('resume');
    try {
      const r = await continuityReviewApi.resume(pid);
      toast.success(`Resuming after chapter ${r.resume_after_chapter ?? 0}`);
      await load();
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setBusy(''); }
  };

  // Poll a running rework preview until it's done/errored.
  useEffect(() => {
    clearInterval(reworkPollRef.current);
    if (preview?.status === 'running') {
      reworkPollRef.current = setInterval(async () => {
        try {
          const r = await continuityReviewApi.getRework(pid, preview.chapter_id, preview.preview_id);
          // `r` is the raw preview doc (keyed by `id`, no `preview_id`/
          // `chapter_number`) — merge rather than replace so those
          // UI-only fields survive each poll tick.
          setPreview((p) => ({ ...p, ...r, preview_id: p.preview_id, chapter_number: p.chapter_number }));
          if (r.status === 'error') toast.error(r.error || 'Rework failed.');
        } catch (e) { toast.error(String(e).slice(0, 180)); }
      }, 3000);
    }
    return () => clearInterval(reworkPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.status, preview?.preview_id]);

  const openRework = async (chapterId) => {
    setReworking(chapterId);
    try {
      const r = await continuityReviewApi.startRework(pid, chapterId, continuityModel || undefined);
      setPreview({ ...r, original: '', rewritten: '' });
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setReworking(null); }
  };

  const recheckChapter = async (chapterId) => {
    setRechecking(chapterId);
    try {
      const r = await continuityReviewApi.recheckChapter(pid, chapterId);
      toast.success(r.finding?.needs_rework ? 'Re-check found issues — rework available below.' : 'Re-check found no issues — chapter looks consistent.');
      await load(true);
      await loadBibleChanges();
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setRechecking(null); }
  };

  const dismissBibleChange = async (characterId) => {
    setDismissing(characterId);
    try {
      await continuityReviewApi.dismissBibleChange(pid, characterId);
      await loadBibleChanges();
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setDismissing(null); }
  };

  const pollBulkRecheck = (characterId, bulkId) => {
    if (bulkPollRef.current[characterId]) clearInterval(bulkPollRef.current[characterId]);
    bulkPollRef.current[characterId] = setInterval(async () => {
      try {
        const r = await continuityReviewApi.getBulkRecheck(pid, characterId, bulkId);
        setBulkRecheck((prev) => ({ ...prev, [characterId]: r }));
        if (r.status === 'done') {
          clearInterval(bulkPollRef.current[characterId]);
          delete bulkPollRef.current[characterId];
          const needingRework = (r.results || []).filter((x) => x.needs_rework).length;
          const failed = (r.results || []).filter((x) => !x.ok).length;
          toast.success(
            `Re-checked ${r.results?.length || 0} chapter(s) for ${bulkRecheckCharacterName(characterId)}`
            + ` — ${needingRework} need rework${failed ? `, ${failed} failed` : ''}.`,
          );
          await load(true);
          await loadBibleChanges();
        }
      } catch (e) {
        clearInterval(bulkPollRef.current[characterId]);
        delete bulkPollRef.current[characterId];
        toast.error(String(e).slice(0, 180));
      }
    }, 3000);
  };

  const bulkRecheckCharacterName = (characterId) =>
    bibleChanges?.characters?.find((g) => g.character_id === characterId)?.character_name || 'this character';

  const startBulkRecheck = async (characterId) => {
    try {
      const r = await continuityReviewApi.startBulkRecheck(pid, characterId);
      setBulkRecheck((prev) => ({ ...prev, [characterId]: { status: 'running', bulk_id: r.bulk_id, total: r.total, results: [] } }));
      pollBulkRecheck(characterId, r.bulk_id);
    } catch (e) { toast.error(String(e).slice(0, 180)); }
  };

  useEffect(() => () => {
    Object.values(bulkPollRef.current).forEach((id) => clearInterval(id));
  }, []);

  const applyRework = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      await continuityReviewApi.applyRework(pid, preview.chapter_id, preview.rewritten);
      toast.success('Rework applied to chapter.');
      setPreview(null);
      await load();
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setApplying(false); }
  };

  const [driftJob, setDriftJob]         = useState(null);  // bulk drift correction job
  const [driftJobId, setDriftJobId]     = useState(null);
  const driftPollRef                     = useRef(null);
  const [autoWalkEnabled, setAutoWalkEnabled] = useState(false);
  const [autoWalkSaving, setAutoWalkSaving]   = useState(false);
  const [walkHistory, setWalkHistory]         = useState([]);
  const [historyOpen, setHistoryOpen]         = useState(false);
  const [historySortKey, setHistorySortKey]   = useState('started_at');
  const [historySortDir, setHistorySortDir]   = useState('desc');

  const startCorrectDrift = async () => {
    if (!pid) return;
    setBusy('correct-drift');
    try {
      const r = await continuityReviewApi.correctDrift(pid);
      if (r.total === 0) {
        toast.info('No chapters are currently flagged for rework.');
        return;
      }
      setDriftJobId(r.job_id);
      setDriftJob(r);
      toast.success(`Drift correction started · ${r.total} chapter${r.total !== 1 ? 's' : ''} to fix`);
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setBusy(''); }
  };

  const resetDriftJob = async () => {
    if (!pid) return;
    setBusy('reset-drift');
    try {
      await continuityReviewApi.resetDrift(pid);
      setDriftJobId(null);
      setDriftJob(null);
      toast.success('Stuck drift job cleared — you can start a fresh run.');
    } catch (e) { toast.error(String(e).slice(0, 180)); }
    finally { setBusy(''); }
  };

  // Poll drift correction job
  useEffect(() => {
    clearInterval(driftPollRef.current);
    if (driftJobId && driftJob?.status === 'running') {
      driftPollRef.current = setInterval(async () => {
        try {
          const r = await continuityReviewApi.getDriftJob(pid, driftJobId);
          setDriftJob(r);
          if (r.status === 'done') {
            toast.success(`Drift correction complete — ${r.corrected} chapter${r.corrected !== 1 ? 's' : ''} fixed${r.skipped ? `, ${r.skipped} skipped` : ''}.`);
            await load(true);
          } else if (r.status === 'error') {
            toast.error(`Drift correction failed: ${r.error || 'unknown error'}`);
          }
        } catch (e) { /* silent */ }
      }, 4000);
    }
    return () => clearInterval(driftPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driftJob?.status, driftJobId, pid]);

  // Load auto-walk setting when project changes
  useEffect(() => {
    if (!pid) return;
    continuityReviewApi.getAutoWalk(pid)
      .then((r) => setAutoWalkEnabled(!!r.enabled))
      .catch(() => {});
    continuityReviewApi.getWalkHistory(pid)
      .then((rows) => setWalkHistory(rows || []))
      .catch(() => {});
  }, [pid]);

  const toggleAutoWalk = async (value) => {
    if (!pid) return;
    setAutoWalkSaving(true);
    try {
      await continuityReviewApi.setAutoWalk(pid, value);
      setAutoWalkEnabled(value);
      toast.success(value
        ? 'Nightly auto-walk enabled for this project.'
        : 'Nightly auto-walk disabled.');
    } catch (e) {
      toast.error(String(e).slice(0, 160));
    } finally {
      setAutoWalkSaving(false);
    }
  };

  const job = data?.job;
  const progressPct = job?.total_chapters
    ? Math.round(((job.chapters_done || 0) / job.total_chapters) * 100)
    : 0;

  return (
    <div className="space-y-4" data-testid="continuity-drift-panel">
      <ManuscriptsHealthTable onPick={pickManuscript} />

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Compass className="h-4 w-4 text-primary" />
          <h3 className="text-[13px] font-semibold">Continuity &amp; Character-Drift Review</h3>
          <span className="text-[10.5px] text-muted-foreground">
            Walks chapters in order, flags plot gaps + character drift against a rolling story-state, and can rework flagged chapters.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            placeholder="Project ID (e.g. proj_abc123…)"
            className="h-8 max-w-sm text-[11.5px]"
            data-testid="continuity-review-pid-input"
          />
          <Button size="sm" variant="outline"
                  disabled={!pid || busy === 'load'}
                  onClick={() => { load(); loadBibleChanges(); }}
                  data-testid="continuity-review-load">
            {busy === 'load' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Load existing
          </Button>
          <Button size="sm"
                  disabled={!pid || busy === 'start' || job?.status === 'queued' || job?.status === 'running'}
                  onClick={start}
                  data-testid="continuity-review-start">
            {busy === 'start' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
            Start walk
          </Button>
          {(job?.status === 'stalled' || job?.status === 'error') && (
            <Button size="sm" variant="secondary"
                    disabled={busy === 'resume'}
                    onClick={resume}
                    data-testid="continuity-review-resume-compact">
              {busy === 'resume' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1 h-3 w-3" />}
              Resume
            </Button>
          )}
          {/* Correct Drift — bulk auto-rework all flagged chapters */}
          {data && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 text-amber-700 border-amber-400/60 hover:bg-amber-50 disabled:opacity-50"
              disabled={!pid || busy === 'correct-drift' || driftJob?.status === 'running'}
              onClick={startCorrectDrift}
              data-testid="continuity-correct-drift-btn"
              title="Auto-rework all chapters flagged as needing rework, in sequence"
            >
              {busy === 'correct-drift' || driftJob?.status === 'running'
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Wrench className="h-3 w-3" />}
              Correct drift
            </Button>
          )}
          {/* Reset — clears a stuck running job so a fresh run can start */}
          {driftJob?.status === 'running' && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1 text-xs text-muted-foreground hover:text-red-600 disabled:opacity-50"
              disabled={busy === 'reset-drift'}
              onClick={resetDriftJob}
              data-testid="continuity-reset-drift-btn"
              title="Clear a stuck drift-correction job so you can start a fresh run"
            >
              {busy === 'reset-drift' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              Reset stuck job
            </Button>
          )}
          <Select value={continuityModel || '__auto'} onValueChange={handleModelChange}>
            <SelectTrigger className="h-8 w-[200px] text-[11px]" data-testid="continuity-model-select">
              <SelectValue placeholder="Continuity model: auto" />
              {modelSaving && <span className="ml-1 text-muted-foreground text-[9px]">saving…</span>}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto">Continuity model: auto</SelectItem>
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.label || m.name || m.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ── Long-chapter warning ──────────────────────────────────── */}
        {longChapters.length > 0 && (
          <div
            className="mt-3 flex gap-2 rounded-lg border border-amber-300/60 bg-amber-50/60 p-2.5 text-[11px] text-amber-800 dark:border-amber-700/30 dark:bg-amber-950/25 dark:text-amber-300"
            data-testid="continuity-long-chapter-warning"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div>
              <span className="font-semibold">Long chapters detected — each may take up to 2 minutes:</span>
              <ul className="mt-1 space-y-0.5 list-disc pl-4">
                {longChapters.map((c) => (
                  <li key={c.id}>
                    Chapter {c.number}{c.title ? ` "${c.title}"` : ''} — {(c.words || 0).toLocaleString()} words
                  </li>
                ))}
              </ul>
              <p className="mt-1 opacity-75">The walk skips automatically if the per-chapter ceiling is hit and marks it for retry on Resume.</p>
            </div>
          </div>
        )}

        {/* ── Prominent Resume banner ──────────────────────────────── */}
        {(job?.status === 'stalled' || job?.status === 'error') && (
          <div
            className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-400/50 bg-amber-50 p-3 dark:border-amber-700/40 dark:bg-amber-950/30"
            data-testid="continuity-review-resume-banner"
          >
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-amber-900 dark:text-amber-200">
                Walk paused — {job.chapters_done ?? 0} of {job.total_chapters ?? '?'} chapters reviewed
              </p>
              <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                Resume will restart from chapter {(job.chapters_done ?? 0) + 1}.
                No already-reviewed chapters will be re-processed or re-billed.
              </p>
              {job.error && (
                <p className="mt-1 truncate text-[10.5px] text-red-700 dark:text-red-400" title={job.error}>
                  Last error: {job.error}
                </p>
              )}
            </div>
            <Button
              className="shrink-0 bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
              size="sm"
              disabled={busy === 'resume'}
              onClick={resume}
              data-testid="continuity-review-resume"
            >
              {busy === 'resume'
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
              Resume Walk
            </Button>
          </div>
        )}

        {job && (
          <div className="mt-3 rounded border border-border/60 bg-panel-header/20 p-2.5" data-testid="continuity-job-status">
            <div className="flex items-center justify-between text-[11px]">
              <span>
                Job <span className="font-mono-stat">{job.id}</span> ·{' '}
                <Badge variant="outline" className={
                  job.status === 'done' ? 'border-emerald-300 text-emerald-800 dark:border-emerald-800/40 dark:text-emerald-200' :
                  (job.status === 'error' || job.status === 'stalled') ? 'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200' :
                  'border-amber-300 text-amber-800 dark:border-amber-800/40 dark:text-amber-200'
                }>{job.status}</Badge>
              </span>
              <span className="text-muted-foreground">
                {job.chapters_done || 0}/{job.total_chapters || 0} chapters
                {job.current_chapter_number != null && (job.status === 'running') ? ` · now on ch. ${job.current_chapter_number}` : ''}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-secondary">
              <div className="h-1.5 rounded-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            {(job.status === 'error' || job.status === 'stalled') && job.error && (
              <div className="mt-1 text-[10.5px] text-red-800 dark:text-red-200">{job.error}</div>
            )}
          </div>
        )}

        {/* ── Drift Correction Progress ──────────────────────────── */}
        {driftJob && (
          <div
            className="mt-3 rounded border border-amber-300/50 bg-amber-50/50 p-2.5 dark:bg-amber-950/20 dark:border-amber-800/30"
            data-testid="continuity-drift-correction-status"
          >
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-900 dark:text-amber-200 mb-1.5">
              <Wrench className="h-3 w-3" />
              Drift Correction
              {' '}
              <Badge variant="outline" className={
                driftJob.status === 'done' ? 'border-emerald-300 text-emerald-800' :
                driftJob.status === 'error' ? 'border-red-300 text-red-800' :
                'border-amber-300 text-amber-800'
              }>{driftJob.status}</Badge>
            </div>
            <div className="flex items-center justify-between text-[10.5px] text-muted-foreground mb-1">
              <span>
                {driftJob.corrected || 0} corrected
                {driftJob.skipped ? ` · ${driftJob.skipped} skipped` : ''}
                {driftJob.current_chapter ? ` · working on ch. ${driftJob.current_chapter}` : ''}
              </span>
              <span>{driftJob.corrected || 0} / {driftJob.total || 0}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-amber-200/60 overflow-hidden">
              <div
                className="h-1.5 rounded-full bg-amber-600 transition-all duration-700"
                style={{ width: `${driftJob.total ? Math.round(((driftJob.corrected || 0) / driftJob.total) * 100) : 0}%` }}
              />
            </div>
            {driftJob.status === 'error' && driftJob.error && (
              <p className="mt-1 text-[10.5px] text-red-800">{driftJob.error}</p>
            )}
          </div>
        )}

        {/* ── Walk History ──────────────────────────────────────── */}
        {data && pid && (
          <div className="mt-3" data-testid="continuity-walk-history-section">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="flex w-full items-center gap-1.5 rounded border border-border/60 bg-muted/20 px-2.5 py-2 text-[11.5px] font-medium text-foreground hover:bg-muted/40 transition-colors"
              data-testid="continuity-walk-history-toggle"
            >
              <History className="h-3.5 w-3.5 text-muted-foreground" />
              Walk History
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0 text-[10px] text-muted-foreground">
                {walkHistory.length}
              </span>
              <span className="ml-auto">
                {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </span>
            </button>
            {historyOpen && (
              <div className="mt-1 rounded border border-border/40 overflow-hidden">
                {walkHistory.length === 0 ? (
                  <p className="px-3 py-4 text-center text-[11px] text-muted-foreground">No walks run yet for this project.</p>
                ) : (
                  <>
                    {/* Sparkline trend chart — show when >= 2 data points */}
                    {walkHistory.length >= 2 && (() => {
                      const chartData = [...walkHistory]
                        .filter(r => r.started_at)
                        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at))
                        .map(r => ({
                          date: new Date(r.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                          issues: r.issues_found ?? 0,
                          fixed: r.rework_applied ?? 0,
                        }));
                      return (
                        <div className="px-3 pt-2.5 pb-1 border-b border-border/40 bg-muted/10" data-testid="walk-history-chart">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Drift trend</p>
                          <ResponsiveContainer width="100%" height={80}>
                            <AreaChart data={chartData} margin={{ top: 2, right: 6, left: -28, bottom: 0 }}>
                              <defs>
                                <linearGradient id="issueGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="fixedGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} />
                              <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                              <YAxis tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} allowDecimals={false} />
                              <Tooltip
                                contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))', background: 'hsl(var(--popover))' }}
                                labelStyle={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}
                              />
                              <Area type="monotone" dataKey="issues" name="Issues" stroke="#f43f5e" strokeWidth={1.5} fill="url(#issueGrad)" dot={{ r: 2, fill: '#f43f5e' }} />
                              <Area type="monotone" dataKey="fixed" name="Fixed" stroke="#10b981" strokeWidth={1.5} fill="url(#fixedGrad)" dot={{ r: 2, fill: '#10b981' }} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })()}
                  <table className="w-full text-[11px]" data-testid="walk-history-table">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border/40">
                        {[
                          { key: 'started_at',    label: 'Date' },
                          { key: 'chapters_done', label: 'Chs' },
                          { key: 'issues_found',  label: 'Issues' },
                          { key: 'rework_applied',label: 'Fixed' },
                          { key: 'status',        label: 'Status' },
                          { key: 'triggered_by',  label: 'Source' },
                        ].map(({ key, label }) => (
                          <th
                            key={key}
                            className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
                            onClick={() => {
                              if (historySortKey === key) {
                                setHistorySortDir((d) => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setHistorySortKey(key);
                                setHistorySortDir('desc');
                              }
                            }}
                          >
                            <span className="inline-flex items-center gap-0.5">
                              {label}
                              {historySortKey === key
                                ? (historySortDir === 'asc'
                                  ? <ArrowUp className="h-2.5 w-2.5" />
                                  : <ArrowDown className="h-2.5 w-2.5" />)
                                : <ArrowUpDown className="h-2.5 w-2.5 opacity-30" />}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...walkHistory].sort((a, b) => {
                        const av = a[historySortKey] ?? '';
                        const bv = b[historySortKey] ?? '';
                        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                        return historySortDir === 'asc' ? cmp : -cmp;
                      }).map((row) => (
                        <tr key={row.id} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                            {row.started_at ? new Date(row.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-center">{row.chapters_done ?? '—'}</td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={row.issues_found > 0 ? 'text-rose-700 dark:text-rose-400 font-semibold' : 'text-muted-foreground'}>
                              {row.issues_found ?? 0}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={row.rework_applied > 0 ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-muted-foreground'}>
                              {row.rework_applied ?? 0}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={[
                              'rounded-full px-1.5 py-0 text-[9.5px] font-medium border',
                              row.status === 'done'    ? 'border-emerald-300 text-emerald-800 bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300' :
                              row.status === 'running' ? 'border-amber-300 text-amber-800 bg-amber-50' :
                              row.status === 'error'   ? 'border-red-300 text-red-800 bg-red-50' :
                              'border-border text-muted-foreground',
                            ].join(' ')}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                            {row.triggered_by === 'auto-walk-cron' ? '🕐 auto' : 'manual'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {data && pid && (
          <div
            className="mt-3 rounded border border-border/60 bg-muted/20 p-2.5"
            data-testid="continuity-auto-walk-section"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[11.5px] font-medium text-foreground">
                <CalendarClock className="h-3.5 w-3.5 text-primary/70" />
                Nightly auto-walk
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoWalkEnabled}
                disabled={autoWalkSaving}
                onClick={() => toggleAutoWalk(!autoWalkEnabled)}
                data-testid="continuity-auto-walk-toggle"
                className={[
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none',
                  autoWalkEnabled ? 'bg-primary' : 'bg-muted-foreground/30',
                  autoWalkSaving ? 'opacity-60' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200',
                    autoWalkEnabled ? 'translate-x-4' : 'translate-x-0',
                  ].join(' ')}
                />
              </button>
            </div>
            <p className="mt-1 text-[10.5px] text-muted-foreground leading-relaxed">
              {autoWalkEnabled
                ? 'The continuity walk will run every night at 02:00 UTC for this project. Any drift found will appear here automatically.'
                : 'Enable to run a continuity check on this project automatically every night at 02:00 UTC.'}
            </p>
          </div>
        )}
      </div>

      {bibleChanges?.characters?.length > 0 && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 p-4 dark:border-amber-800/40 dark:bg-amber-950/20" data-testid="continuity-bible-changes-banner">
          <div className="mb-2 flex items-center gap-2">
            <BookUser className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            <h3 className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">Character Bible changed since last review</h3>
            <span className="text-[10.5px] text-muted-foreground">
              Nothing re-runs automatically — pick a chapter below to re-check it against the updated bible, then rework if needed.
            </span>
          </div>
          <div className="space-y-2">
            {bibleChanges.characters.map((grp) => {
              const bulk = bulkRecheck[grp.character_id];
              const bulkRunning = bulk?.status === 'running';
              return (
              <div key={grp.character_id} className="rounded border border-amber-300/50 bg-card/60 p-2.5" data-testid={`continuity-bible-change-${grp.character_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11.5px]">
                    <span className="font-semibold">{grp.character_name}</span>
                    <span className="ml-1.5 text-muted-foreground">— {grp.fields_changed.join(', ')} changed</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {grp.affected_chapters?.length > 1 && (
                      <Button size="sm" variant="secondary" className="h-6 px-2 text-[10.5px]"
                              disabled={bulkRunning}
                              onClick={() => startBulkRecheck(grp.character_id)}
                              data-testid={`continuity-bible-change-recheck-all-${grp.character_id}`}>
                        {bulkRunning ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ListChecks className="mr-1 h-3 w-3" />}
                        {bulkRunning ? `Re-checking ${bulk.results?.length || 0}/${bulk.total}…` : `Re-check all (${grp.affected_chapters.length})`}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10.5px]"
                            disabled={dismissing === grp.character_id || bulkRunning}
                            onClick={() => dismissBibleChange(grp.character_id)}
                            data-testid={`continuity-bible-change-dismiss-${grp.character_id}`}>
                      {dismissing === grp.character_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <X className="mr-1 h-3 w-3" />}
                      Dismiss
                    </Button>
                  </div>
                </div>
                {grp.affected_chapters?.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {grp.affected_chapters.map((ch) => (
                      <Button key={ch.chapter_id} size="sm" variant="outline" className="h-6 px-2 text-[10.5px]"
                              disabled={rechecking === ch.chapter_id || bulkRunning}
                              onClick={() => recheckChapter(ch.chapter_id)}
                              data-testid={`continuity-bible-change-recheck-${ch.chapter_id}`}>
                        {rechecking === ch.chapter_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                        Ch. {ch.chapter_number} — Re-check &amp; rework
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-[10px] text-muted-foreground">No reviewed chapters mention this character yet.</div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-[12px] font-semibold">
              Project {data.project_id} · {data.findings?.length || 0} chapter{(data.findings?.length || 0) === 1 ? '' : 's'} reviewed
              {data.needs_rework_count > 0 && (
                <span className="ml-2 text-[10.5px] font-normal text-orange-700 dark:text-orange-300">
                  · {data.needs_rework_count} need{data.needs_rework_count === 1 ? 's' : ''} rework
                </span>
              )}
            </span>
          </div>
          {(!data.findings || data.findings.length === 0) ? (
            <div className="text-[11px] text-muted-foreground">
              No findings yet — click "Start walk" to review chapters in order.
            </div>
          ) : data.findings.map((f) => {
            const isOpen = expanded[f.chapter_id];
            const flagged = (f.gaps?.length || 0) + (f.character_drift?.length || 0) + (f.pacing_issues?.length || 0) + (f.pacing_trend ? 1 : 0);
            return (
              <div key={f.chapter_id}
                   className="mb-2 rounded border border-border/60"
                   data-testid={`continuity-chapter-${f.chapter_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((x) => ({ ...x, [f.chapter_id]: !isOpen }))}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11.5px] hover:bg-secondary"
                >
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <span>Ch. {f.chapter_number} — {f.title}</span>
                  <span className="ml-auto flex items-center gap-1.5">
                    {f.status === 'reworked' && (
                      <Badge variant="outline" className="text-[9.5px] border-sky-300 text-sky-800 dark:border-sky-800/40 dark:text-sky-200">reworked</Badge>
                    )}
                    {f.status === 'error' && (
                      <Badge variant="outline" className="text-[9.5px] border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200">error</Badge>
                    )}
                    {f.status === 'done' && (
                      <Badge variant="outline" className={`text-[9.5px] ${flagged ? SEVERITY_CLS.moderate : 'border-emerald-300 text-emerald-800 dark:border-emerald-800/40 dark:text-emerald-200'}`}>
                        {flagged ? `${flagged} flagged` : 'clean'}
                      </Badge>
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div className="space-y-2 border-t border-border/40 p-2.5 text-[10.5px]">
                    {f.status === 'error' ? (
                      <div className="text-red-800 dark:text-red-200">Error: {f.error}</div>
                    ) : (
                      <>
                        {f.verdict && <p className="whitespace-pre-wrap text-muted-foreground">{f.verdict}</p>}
                        {f.gaps?.length > 0 && (
                          <div>
                            <div className="mb-0.5 font-semibold">Gaps</div>
                            <ul className="list-disc space-y-0.5 pl-4">
                              {f.gaps.map((g, i) => <li key={i}>{g}</li>)}
                            </ul>
                          </div>
                        )}
                        {f.character_drift?.length > 0 && (
                          <div>
                            <div className="mb-0.5 font-semibold">Character drift</div>
                            <ul className="space-y-1">
                              {f.character_drift.map((d, i) => (
                                <li key={i} className="flex flex-wrap items-center gap-1.5">
                                  <Badge variant="outline" className={`text-[9px] ${SEVERITY_CLS[d.severity] || SEVERITY_CLS.low}`}>{d.severity}</Badge>
                                  <span className="font-semibold">{d.character}:</span>
                                  <span>{d.issue}</span>
                                  {d.quote && <span className="italic text-muted-foreground">"{d.quote}"</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(typeof f.pacing_score === 'number' || f.pacing_issues?.length > 0 || f.pacing_trend) && (
                          <div data-testid={`continuity-pacing-${f.chapter_id}`}>
                            <div className="mb-0.5 flex items-center gap-1.5 font-semibold">
                              Pacing
                              {typeof f.pacing_score === 'number' && (
                                <Badge variant="outline" className={`text-[9px] ${f.pacing_score >= 70 ? 'border-emerald-300 text-emerald-800 dark:border-emerald-800/40 dark:text-emerald-200' : f.pacing_score >= 55 ? SEVERITY_CLS.low : SEVERITY_CLS.moderate}`}>
                                  {f.pacing_score}/100{f.pacing_direction && f.pacing_direction !== 'ok' ? ` · ${f.pacing_direction}` : ''}
                                </Badge>
                              )}
                            </div>
                            {f.pacing_trend && (
                              <p className="mb-1 font-medium text-amber-800 dark:text-amber-300">{f.pacing_trend}</p>
                            )}
                            {f.pacing_issues?.length > 0 && (
                              <ul className="space-y-1">
                                {f.pacing_issues.map((p, i) => (
                                  <li key={i} className="flex flex-wrap items-center gap-1.5">
                                    <Badge variant="outline" className={`text-[9px] ${SEVERITY_CLS[p.severity] || SEVERITY_CLS.low}`}>{p.severity}</Badge>
                                    <span>{p.issue}</span>
                                    {p.quote && <span className="italic text-muted-foreground">"{p.quote}"</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {(f.characters_created?.length > 0 || f.characters_updated?.length > 0) && (
                          <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground" data-testid={`continuity-characters-touched-${f.chapter_id}`}>
                            <BookUser className="h-3 w-3" />
                            {f.characters_created?.length > 0 && (
                              <span>Created: {f.characters_created.join(', ')}</span>
                            )}
                            {f.characters_updated?.length > 0 && (
                              <span>{f.characters_created?.length > 0 ? ' · ' : ''}Enriched: {f.characters_updated.join(', ')}</span>
                            )}
                          </div>
                        )}
                        {(f.gaps?.length > 0 || f.character_drift?.length > 0 || f.pacing_issues?.length > 0 || f.pacing_trend) && f.status !== 'reworked' && (
                          <Button size="sm" variant="outline" className="mt-1 h-7 text-[10.5px]"
                                  disabled={reworking === f.chapter_id}
                                  onClick={() => openRework(f.chapter_id)}
                                  data-testid={`continuity-rework-${f.chapter_id}`}>
                            {reworking === f.chapter_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Wand2 className="mr-1 h-3 w-3" />}
                            Rewrite chapter
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="continuity-rework-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[14px]">
              Rework preview — Chapter {preview?.chapter_number}
            </DialogTitle>
          </DialogHeader>
          {preview?.reasoning && (
            <div className="rounded border border-sky-300/50 bg-sky-50/60 px-2.5 py-1.5 text-[11px] text-sky-900 dark:border-sky-800/40 dark:bg-sky-950/20 dark:text-sky-200" data-testid="continuity-rework-reasoning">
              {preview.reasoning}
            </div>
          )}
          {preview?.status === 'running' && (
            <div className="flex items-center gap-2 py-8 text-[12px] text-muted-foreground" data-testid="continuity-rework-generating">
              <Loader2 className="h-4 w-4 animate-spin" />
              Rewriting the chapter — full-chapter rewrites can take a minute or two…
            </div>
          )}
          {preview?.status === 'error' && (
            <div className="py-4 text-[11.5px] text-red-800 dark:text-red-200" data-testid="continuity-rework-error">
              Rework failed: {preview.error}
            </div>
          )}
          {preview?.status === 'done' && (
            <div className="grid max-h-[55vh] gap-3 overflow-y-auto sm:grid-cols-2">
              <div>
                <div className="mb-1 text-[10.5px] font-semibold text-muted-foreground">Original</div>
                <Textarea readOnly value={preview.original} className="h-64 text-[10.5px]" data-testid="continuity-rework-original" />
              </div>
              <div>
                <div className="mb-1 text-[10.5px] font-semibold text-muted-foreground">Rewritten</div>
                <Textarea readOnly value={preview.rewritten} className="h-64 text-[10.5px]" data-testid="continuity-rework-rewritten" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPreview(null)} data-testid="continuity-rework-discard">
              Discard
            </Button>
            <Button size="sm" disabled={applying || preview?.status !== 'done'} onClick={applyRework} data-testid="continuity-rework-apply">
              {applying ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Apply to chapter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
